import { usePermissionsStore } from "../../permissions/permissions_store";
import SurveyResponseOffcanvas from "../SurveyResponseOffcanvas";
import SurveysService from "../../../services/surveys.service";
import { answerText, csvSafe, itemExport } from "../item-list-model";
import React, {
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Image } from "uikit-react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { CSVLink } from "react-csv";

import { AppContexts } from "../../../providers";
import GuestMessages from "../../guests/guest_messages";

const Responses = () => {
  const { canEdit } = usePermissionsStore();
  const canHandle = canEdit("messaging.surveys");
  const canMessage = canHandle && canEdit("messaging.guests_message");
  const surveysContext = useContext(AppContexts.SurveysContext);
  const reservationContext = useContext(AppContexts.ReservationsContext);
  const { user } = useContext(AppContexts.AuthContext);

  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [view, setView] = useState("overview");
  const [sortField, setSortField] = useState("submitted_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const history = useHistory();
  const location = useLocation();
  const surveyId = location.pathname.split("/")[2];
  const responseId = new URLSearchParams(location.search).get("response");
  const hasResponse = responseId !== null;
  const [selectedResponseId, setSelectedResponseId] = useState(null);
  const [loadedSurvey, setLoadedSurvey] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    document.title = "Survey Result - RueBaRue";
  }, []);

  useEffect(() => {
    setSelectedResponseId(null);
  }, [surveyId, responseId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    setLoadedSurvey({});
    if (hasResponse) setView("list");
    if (hasResponse && !/^[1-9]\d*$/.test(responseId)) {
      setLoadError("This survey response link is invalid.");
      setLoading(false);
      return () => { active = false; };
    }
    const request = hasResponse
      ? SurveysService.getSurveyResponse(surveyId, responseId)
      : SurveysService.getSurveyAndResult(surveyId, startDate, endDate);
    request.then(({ data }) => {
      if (!active) return;
      setLoadedSurvey(data);
    }).catch((error) => {
      if (active) setLoadError(error.response?.status === 404 ? "This survey response is unavailable." : "Unable to load survey responses. Please try again.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [surveyId, responseId, hasResponse, startDate, endDate, refresh]);

  const handleBack = useCallback(() => {
    history.goBack();
  }, []);

  const survey = useMemo(() => {
    const surveyResult = loadedSurvey;

    if (!surveyResult) {
      return {};
    }

    const updatedSurvey = { ...surveyResult };
    const { question_order: questionOrder, questions } = surveyResult;

    if (questionOrder) {
      const questionOrderList = questionOrder.split(",");

      updatedSurvey.questions = questionOrderList
        .map((questionId) =>
          questions?.find(
            ({ survey_question_id: surveyQuestionId, deleted }) =>
              +surveyQuestionId === +questionId && !deleted,
          ),
        )
        .filter(Boolean);

      updatedSurvey.questions.forEach((question) => {
        const {
          option_order: optionOrder,
          question_options: questionOptions,
        } = question;

        if (optionOrder) {
          const optionOrderList = optionOrder.split(",");

          question.question_options = optionOrderList
            .map((optionId) =>
              questionOptions?.find(
                ({
                  survey_question_option_id: questionOptionId,
                  type,
                  deleted,
                }) =>
                  (+questionOptionId === +optionId || +type === +optionId) &&
                  !deleted,
              ),
            )
            .filter(Boolean);
        }
      });
    }
    updatedSurvey.survey_results = updatedSurvey.survey_results ?? [];

    return updatedSurvey;
  }, [loadedSurvey]);

  const responses = useMemo(() => {
    if (!survey) {
      return [];
    } else {
      return survey.survey_results || [];
    }
  }, [survey]);

  const questions = useMemo(() => {
    let qs = (survey.question_order || "")
      .split(",")
      .map((id) => {
        let q = (survey.questions || []).find(
          (i) => +i.survey_question_id === +id,
        );

        if (!q) {
          return;
        }

        return {
          id: +q.survey_question_id,
          title: q.question,
          help_text: q.help_text,
          options: q.question_options,
          type: q.question_type,
          responses: {
            sum: 0,
            guest_reservation_ids: [],
            answers: {},
          },
        };
      })
      .filter((q) => !!q);

    responses.map((r) => {
      (r.answers || []).map((a) => {
        let q = qs.find((i) => +i.id === +a.survey_question_id);
        if (!q || !q.responses || a.value) {
          return;
        }

        let rs = q.responses;

        if (!rs.guest_reservation_ids.includes(+r.guest_reservation_id)) {
          rs.sum += 1;
          rs.guest_reservation_ids.push(+r.guest_reservation_id);
        }

        if (rs.answers.hasOwnProperty([a.answer])) {
          rs.answers[a.answer] += 1;
        } else {
          if (q.type === "short-answer") {
            rs.answers[a.answer || a.custom_answer] = 1;
          } else {
            rs.answers[a.answer.toString()] = 1;
          }
        }
      });
    });

    return qs;
  }, [responses]);

  const headers = useMemo(() => {
    if (questions.length == 0) return [];
    return [
      "Submitted At",
      "First Name",
      "Last Name",
      "Check-In",
      "Check-Out",
      "Property ID",
      "Property Address",
      "Reservation ID",
      ...questions.map((q) => q.title),
    ];
  }, [questions]);

  const data = useMemo(() => {
    return responses.map((r) => {
      const reservation = r.reservation || {};
      let answers = questions.map(
        (q) =>
          answerText(r.answers, q.id),
      );
      return [
        moment.utc(r.created_at).local().format("MM/DD/YYYY h:mm"),
        reservation?.first_name,
        reservation?.last_name,
        reservation?.check_in,
        reservation?.check_out,
        reservation?.pms_id,
        reservation?.rental_address || "",
        user?.details?.pms === "Streamline" ? reservation?.native_id : reservation?.reservation_id,
        ...answers,
      ].map(csvSafe);
    });
  }, [questions, responses]);

  const answers = useMemo(() => {
    return responses.map((r) => {
      let answers = questions.map(
        (q) =>
          answerText(r.answers, q.id),
      );
      return answers || [];
    });
  }, [questions, responses]);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }, [sortField]);

  const sortedResponses = useMemo(() => {
    if (!sortField || !responses.length) return responses;

    const sorted = [...responses].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case "submitted_at":
          valA = new Date(a.created_at).getTime();
          valB = new Date(b.created_at).getTime();
          break;
        case "guest_name":
          valA = `${a.reservation?.first_name || ""} ${a.reservation?.last_name || ""}`.toLowerCase();
          valB = `${b.reservation?.first_name || ""} ${b.reservation?.last_name || ""}`.toLowerCase();
          break;
        case "check_in":
          valA = new Date(a.reservation?.check_in).getTime() || 0;
          valB = new Date(b.reservation?.check_in).getTime() || 0;
          break;
        case "property_id":
          valA = (a.reservation?.pms_id || "").toLowerCase();
          valB = (b.reservation?.pms_id || "").toLowerCase();
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [responses, sortField, sortDirection]);

  const sortedAnswers = useMemo(() => {
    if (!sortField || !responses.length) return answers;

    const indexed = responses.map((r, i) => ({ r, i }));
    const sorted = [...indexed].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case "submitted_at":
          valA = new Date(a.r.created_at).getTime();
          valB = new Date(b.r.created_at).getTime();
          break;
        case "guest_name":
          valA = `${a.r.reservation?.first_name || ""} ${a.r.reservation?.last_name || ""}`.toLowerCase();
          valB = `${b.r.reservation?.first_name || ""} ${b.r.reservation?.last_name || ""}`.toLowerCase();
          break;
        case "check_in":
          valA = new Date(a.r.reservation?.check_in).getTime() || 0;
          valB = new Date(b.r.reservation?.check_in).getTime() || 0;
          break;
        case "property_id":
          valA = (a.r.reservation?.pms_id || "").toLowerCase();
          valB = (b.r.reservation?.pms_id || "").toLowerCase();
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted.map(({ i }) => answers[i]);
  }, [answers, responses, sortField, sortDirection]);

  const sortIcon = useCallback((field) => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  }, [sortField, sortDirection]);

  const CHART_COLORS = [
    "#569946", "#4A90D9", "#D94A4A", "#D9A74A", "#7B4AD9",
    "#4AD9D9", "#D94A90", "#90D94A", "#D9D94A", "#4A4AD9",
  ];

  const formatData = (question) => {
    let answers = question.responses.answers || {};
    return Object.keys(answers)
      .map((k) => ({ name: k, value: answers[k] }))
      .sort((a, b) => (b.name > a.name ? 1 : -1));
  };

  const formatRangeData = (question, range) => {
    let answers = question.responses.answers || {};
    return new Array(range).fill(0).map((_, i) => ({
      name: String(i + 1),
      value: answers[i + 1] || 0,
    }));
  };

  const updateView = (view) => {
    setView(view);
    if (hasResponse) history.push(location.pathname);
  };

  const updateSelected = (idx) => {
    if (idx >= 0 && idx < survey.survey_results.length) {
      setSelectedResponseId(String(survey.survey_results[idx].survey_results_id));
    }
  };

  const handleSMS = (reservation) => {
    if (!canMessage) return;
    reservationContext.setReservation(reservation);
  };

  const handleResult = async (result) => {
    if (!canHandle) return;
    await surveysContext.flagSurveyResult(result.survey_results_id);
    setRefresh((value) => value + 1);
  }

  const chartRenderer = useCallback(
    (question) => {
      const data = question.type.startsWith("range-")
        ? formatRangeData(question, question.type === "range-5" ? 5 : 10)
        : formatData(question);

      return (
        <div className="chart-wrapper">
          {question.type === "multiple-choice" && (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={2}
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                >
                  {data.map((_, idx) => (
                    <Cell
                      key={idx}
                      fill={CHART_COLORS[idx % CHART_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}

          {question.type === "checkboxes" && (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip />
                <Bar dataKey="value" fill="#569946" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}

          {(question.type === "range-5" || question.type === "range-10") && (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#4A90D9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}

          {question.type === "short-answer" && (
            <div className="chart">
              <ul className="uk-list uk-list-striped">
                {Object.keys(question.responses.answers)
                  .filter((answer) => !!answer)
                  .map((answer) => {
                    return <li key={answer}>{answer}</li>;
                  })}
              </ul>
            </div>
          )}
        </div>
      );
    },
    [questions],
  );

  const listRenderer = (result, index) => {
    let { reservation } = result;
    const rowAnswers = sortedAnswers[index] || [];
    return (
      <tr key={index}>
        <td className="hms-action-btn">
          <button
            className="uk-button"
            onClick={() => updateSelected(responses.findIndex((r) => r.survey_results_id === result.survey_results_id))}
            aria-label="Response Details"
            uk-tooltip="Response Details"
          >
            <Image src="/images/detail.svg" />
          </button>
          {canMessage && reservation.formatted_phone && reservation.formatted_phone.trim() && (
            <div className="uk-inline">
              <button
                className="uk-button"
                uk-toggle="target: #modal-guest-messages"
                onClick={() => handleSMS(reservation)}
                uk-tooltip="Send Text"
              >
                <Image src="/images/sms.svg" />
              </button>
            </div>
          )}
          {canHandle && (<div class="uk-inline">
            <button
              class="uk-button"
              disabled={!canHandle}
              aria-label={result.flagged ? "Mark Unhandled" : "Mark Handled"}
              uk-tooltip="Mark Handled"
              title=""
              aria-describedby="uk-tooltip-755"
              aria-expanded="false"
              onClick={() => {
                handleResult(result);
              }}
            >
              <img
                data-src={
                  result.flagged
                    ? "/images/fulfilled.svg"
                    : "/images/unfulfilled.svg"
                }
                data-uk-img=""
                loading="lazy"
                src={
                  result.flagged
                    ? "/images/fulfilled.svg"
                    : "/images/unfulfilled.svg"
                }
              />
            </button>
          </div>)}
        </td>
        <td>
          <div>{moment.utc(result.created_at).local().format("MM/DD/YYYY h:mm")}</div>
        </td>
        <td>{`${reservation?.first_name || ""} ${
          reservation?.last_name || ""
        }`}</td>
        <td>
          {moment.utc(reservation?.check_in).format("MM/DD/YYYY")} -
          {moment.utc(reservation?.check_out).format("MM/DD/YYYY")}
        </td>
        <td>{reservation?.pms_id || ""}</td>
        <td>{reservation?.rental_address || ""}</td>
        <td>{user?.details?.pms === "Streamline" ? (reservation?.native_id || "") : (reservation?.reservation_id || "")}</td>
        {rowAnswers.map((answer, i) => 
          <td className="message" key={i}>
            <textarea
              rows={1}
              value={answer}
              disabled
            />
          </td>
        )}
      </tr>
    );
  };

  return (
    <div id="surveys-detail-template">
      <div id="hms-page-title">
        <div>
          <div className="uk-align-left">
            <div className="uk-flex uk-flex-middle">
              <button
                type="button"
                className="uk-button hms-img-btn go-back-arrow-btn"
                onClick={handleBack}
              >
                <span uk-icon="icon: arrow-left; ratio: 1"></span>
              </button>
              <h1 className="uk-heading-small">{survey?.name}</h1>
            </div>
          </div>
          {!hasResponse && <div className="uk-align-right">
            <div className="uk-inline hms-date-picker uk-margin-small-right">
              <input
                className="uk-input"
                type="date"
                id="startDate"
                aria-label="Start date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="uk-inline hms-date-picker uk-margin-small-right">
              <input
                className="uk-input"
                type="date"
                id="endDate"
                aria-label="End date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            {survey.survey_results_count > 0 && (<>
              <CSVLink
                data={data}
                filename={`${moment().unix()}_survey_response.csv`}
                headers={headers}
              >
                <button className="download">
                  <img src="/images/download.svg" uk-tooltip="Download CSV" />
                </button>
              </CSVLink>
              {responses.some((r) => r.answers?.some((a) => a.value)) && <CSVLink
                className="uk-button hms-btn uk-margin-small-left" filename="survey-request-items.csv"
                headers={["Submission ID", "Submitted At", "First Name", "Last Name", "Property", "Arrival", "Category", "Item", "Quantity", "Unit", "Preference", "Category Notes"]}
                data={itemExport(responses)}>Export Selected Items</CSVLink>}
            </>)}
          </div>}
        </div>
      </div>
      <div id="hms-main-body">
        {!hasResponse && loading && <div role="status" className="uk-padding-small"><span data-uk-spinner="ratio: 0.7" /> Loading survey responses…</div>}
        {!hasResponse && loadError && <div role="alert" className="uk-alert uk-alert-danger">{loadError}<button type="button" className="uk-button uk-button-text uk-margin-left" onClick={() => setRefresh((value) => value + 1)}>Try again</button></div>}
        {!hasResponse && <ul
          className="survey-tabs uk-margin-small-bottom uk-flex-left"
          data-uk-tab
        >
          <li
            className="survey-tab uk-active"
            onClick={() => {
              updateView("overview");
            }}
          >
            <a href="#" onClick={(event) => event.preventDefault()}>Overview</a>
          </li>
          <li
            className="survey-tab"
            onClick={() => {
              updateView("list");
            }}
          >
            <a href="#" onClick={(event) => event.preventDefault()}>List</a>
          </li>
        </ul>}

        {view === "overview" && !loading && !loadError && (
          <div>
            {survey.survey_results_count == 0 ? (
              <div className="survey-no-response">
                No survey responses for this date range
              </div>
            ) : (
              questions.map((question) => {
                return (
                  question.responses.sum > 0 && (
                    <div>
                      <h4>{question.title}</h4>
                      <div>{question.responses.sum} Reponse(s)</div>
                      <div>{chartRenderer(question)}</div>
                    </div>
                  )
                );
              })
            )}
          </div>
        )}

        {view === "list" && !loading && !loadError && (
          <div>
            {survey.survey_results_count == 0 ? (
              <div className="survey-no-response">
                No survey responses for this date range
              </div>
            ) : (
              <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
                <div className="uk-overflow-auto">
                  <table className="uk-table uk-table-small uk-table-hover uk-table-divider hms-table">
                    <thead>
                      <tr>
                        <th className="actions">Actions</th>
                        <th className="hms-sortable-th" onClick={() => handleSort("submitted_at")} style={{ cursor: "pointer" }}>
                          Submitted At{sortIcon("submitted_at")}
                        </th>
                        <th className="hms-sortable-th" onClick={() => handleSort("guest_name")} style={{ cursor: "pointer" }}>
                          Guest Name{sortIcon("guest_name")}
                        </th>
                        <th className="hms-sortable-th" onClick={() => handleSort("check_in")} style={{ cursor: "pointer" }}>
                          Check-In - Check-Out{sortIcon("check_in")}
                        </th>
                        <th className="hms-sortable-th" onClick={() => handleSort("property_id")} style={{ cursor: "pointer" }}>
                          Property Id{sortIcon("property_id")}
                        </th>
                        <th className="">Property Address</th>
                        <th className="">Reservation Id</th>
                        {
                          questions?.map((q, idx) => (
                            <th className="message" key={q.id} uk-tooltip={q?.title}>
                              Q{idx + 1}
                            </th>
                          ))
                        }
                      </tr>
                    </thead>
                    <tbody>{sortedResponses.map(listRenderer)}</tbody>
                  </table>
                </div>
                <div className="uk-margin-top">
                  <span>{responses.length || 0} Result(s)</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <SurveyResponseOffcanvas open={hasResponse || selectedResponseId !== null} survey={survey}
        result={responses.find((result) => String(result.survey_results_id) === (hasResponse ? responseId : selectedResponseId))}
        loading={loading} error={loadError} showMessage
        onClose={() => {
          setSelectedResponseId(null);
          if (hasResponse) {
            const params = new URLSearchParams(location.search);
            params.delete("response");
            history.replace({ pathname: location.pathname, search: params.toString() });
            setView("list");
          }
        }}
        onRetry={() => setRefresh((value) => value + 1)} />
      {canMessage && <GuestMessages />}
    </div>
  );
};

export default Responses;
